import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { Row, Col, Card, Button, ProgressBar, Table, Badge } from 'react-bootstrap';
import { uploadFile } from '../services/api';

class Upload extends Component {
  constructor(props) {
    super(props);
    this.state = {
      files: [],
      dragging: false,
      uploading: false,
      currentFileIndex: 0,
      fileStatuses: {}, // { filename: { status, progress, results, error } }
      completedCount: 0,
      errorCount: 0,
    };
    this.fileInputRef = React.createRef();
  }

  handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ dragging: true });
  };

  handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ dragging: false });
  };

  handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ dragging: false });

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      this.handleFilesSelect(droppedFiles);
    }
  };

  handleFileInputChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 0) {
      this.handleFilesSelect(selectedFiles);
    }
  };

  handleFilesSelect = (newFiles) => {
    const allowedExtensions = ['.csv', '.json'];
    const validFiles = [];
    const invalidFiles = [];

    newFiles.forEach(file => {
      const extension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
      if (allowedExtensions.includes(extension)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      this.props.showAlert(
        `Skipped ${invalidFiles.length} invalid file(s). Only CSV and JSON allowed.`,
        'warning'
      );
    }

    if (validFiles.length > 0) {
      // Initialize status for each file
      const fileStatuses = { ...this.state.fileStatuses };
      validFiles.forEach(file => {
        fileStatuses[file.name] = {
          status: 'pending',
          progress: 0,
          results: null,
          error: null,
        };
      });

      this.setState(prevState => ({
        files: [...prevState.files, ...validFiles],
        fileStatuses,
      }));
    }
  };

  removeFile = (index) => {
    this.setState(prevState => {
      const files = [...prevState.files];
      const removedFile = files.splice(index, 1)[0];
      const fileStatuses = { ...prevState.fileStatuses };
      delete fileStatuses[removedFile.name];
      return { files, fileStatuses };
    });
  };

  handleUploadAll = async () => {
    const { files } = this.state;
    if (files.length === 0) return;

    this.setState({ 
      uploading: true, 
      currentFileIndex: 0,
      completedCount: 0,
      errorCount: 0,
    });

    let completed = 0;
    let errors = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      this.setState({ currentFileIndex: i });

      // Update status to uploading
      this.updateFileStatus(file.name, { status: 'uploading', progress: 0 });

      try {
        const results = await uploadFile(file, (progress) => {
          this.updateFileStatus(file.name, { progress });
        });

        this.updateFileStatus(file.name, { 
          status: 'completed', 
          progress: 100, 
          results 
        });
        completed++;
        this.setState({ completedCount: completed });

      } catch (error) {
        const errorMsg = error.response?.data?.error || 'Upload failed';
        this.updateFileStatus(file.name, { 
          status: 'error', 
          error: errorMsg 
        });
        errors++;
        this.setState({ errorCount: errors });
      }
    }

    this.setState({ uploading: false });
    
    if (errors === 0) {
      this.props.showAlert(`Successfully processed ${completed} file(s)!`, 'success');
    } else {
      this.props.showAlert(
        `Completed: ${completed}, Failed: ${errors}`,
        errors === files.length ? 'danger' : 'warning'
      );
    }
  };

  updateFileStatus = (filename, updates) => {
    this.setState(prevState => ({
      fileStatuses: {
        ...prevState.fileStatuses,
        [filename]: {
          ...prevState.fileStatuses[filename],
          ...updates,
        },
      },
    }));
  };

  handleReset = () => {
    this.setState({
      files: [],
      fileStatuses: {},
      completedCount: 0,
      errorCount: 0,
    });
    if (this.fileInputRef.current) {
      this.fileInputRef.current.value = '';
    }
  };

  getStatusBadge = (status) => {
    const variants = {
      pending: 'secondary',
      uploading: 'primary',
      completed: 'success',
      error: 'danger',
    };
    const labels = {
      pending: 'Pending',
      uploading: 'Processing...',
      completed: 'Done',
      error: 'Failed',
    };
    return <Badge variant={variants[status]}>{labels[status]}</Badge>;
  };

  getTotalStats = () => {
    const { fileStatuses } = this.state;
    let totalRows = 0;
    let totalAnomalies = 0;
    let totalIssues = 0;

    Object.values(fileStatuses).forEach(({ results }) => {
      if (results) {
        totalRows += results.original_data_stats?.shape?.[0] || 0;
        totalAnomalies += results.anomalies?.ml_based?.total_anomalies || 0;
        totalIssues += results.validation?.summary?.total_issues || 0;
      }
    });

    return { totalRows, totalAnomalies, totalIssues };
  };

  render() {
    const { files, dragging, uploading, fileStatuses, currentFileIndex, completedCount, errorCount } = this.state;
    const hasResults = Object.values(fileStatuses).some(s => s.status === 'completed');

    return (
      <div>
        <h2 className="mb-4">Upload Data</h2>

        {/* Drop Zone */}
        <Row className="mb-4">
          <Col md={12}>
            <Card>
              <Card.Body>
                <div
                  className={`upload-zone ${dragging ? 'dragging' : ''}`}
                  onDragEnter={this.handleDragEnter}
                  onDragLeave={this.handleDragLeave}
                  onDragOver={this.handleDragOver}
                  onDrop={this.handleDrop}
                  onClick={() => this.fileInputRef.current?.click()}
                >
                  <div className="upload-icon">📁</div>
                  <h4>Drop files here</h4>
                  <p className="text-muted">or click to browse</p>
                  <p className="text-muted small">
                    Supports multiple CSV and JSON files up to 50MB each
                  </p>
                  <input
                    type="file"
                    ref={this.fileInputRef}
                    onChange={this.handleFileInputChange}
                    accept=".csv,.json"
                    multiple
                    style={{ display: 'none' }}
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* File Queue */}
        {files.length > 0 && (
          <Card className="mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>
                <strong>Files Queue</strong> ({files.length} file{files.length !== 1 ? 's' : ''})
              </span>
              <div>
                {!uploading && (
                  <>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="mr-2"
                      onClick={this.handleReset}
                    >
                      Clear All
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={this.handleUploadAll}
                      disabled={files.length === 0}
                    >
                      🚀 Process All ({files.length})
                    </Button>
                  </>
                )}
              </div>
            </Card.Header>
            <Card.Body>
              {/* Overall Progress */}
              {uploading && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span>Overall Progress</span>
                    <span>{currentFileIndex + 1} / {files.length}</span>
                  </div>
                  <ProgressBar 
                    now={((currentFileIndex + 1) / files.length) * 100} 
                    animated 
                    striped 
                  />
                </div>
              )}

              {/* File List */}
              <Table size="sm" hover>
                <thead>
                  <tr>
                    <th>Filename</th>
                    <th>Size</th>
                    <th>Status</th>
                    <th>Progress</th>
                    <th>Results</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((file, index) => {
                    const status = fileStatuses[file.name] || {};
                    return (
                      <tr key={index}>
                        <td>
                          <span className="mr-2">
                            {file.name.endsWith('.json') ? '📄' : '📊'}
                          </span>
                          {file.name}
                        </td>
                        <td>{(file.size / 1024).toFixed(1)} KB</td>
                        <td>{this.getStatusBadge(status.status || 'pending')}</td>
                        <td style={{ width: '150px' }}>
                          {status.status === 'uploading' && (
                            <ProgressBar 
                              now={status.progress} 
                              size="sm" 
                              animated 
                            />
                          )}
                          {status.status === 'completed' && (
                            <ProgressBar now={100} variant="success" />
                          )}
                          {status.status === 'error' && (
                            <span className="text-danger small">{status.error}</span>
                          )}
                        </td>
                        <td>
                          {status.results && (
                            <span className="small">
                              {status.results.original_data_stats?.shape?.[0] || 
                               status.results.original_shape?.[0] || 0} rows, 
                              {' '}{status.results.anomalies?.ml_based?.total_anomalies || 0} anomalies
                            </span>
                          )}
                        </td>
                        <td>
                          {!uploading && status.status === 'pending' && (
                            <Button 
                              size="sm" 
                              variant="outline-danger"
                              onClick={() => this.removeFile(index)}
                            >
                              ✕
                            </Button>
                          )}
                          {status.results?.download_url && (
                            <a
                              href={status.results.download_url}
                              className="btn btn-sm btn-outline-success mr-1"
                              download
                            >
                              ⬇ Clean
                            </a>
                          )}
                          {status.results?.job_id && (
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => this.props.history.push(`/jobs/${status.results.job_id}`)}
                            >
                              View
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}

        {/* Summary Stats */}
        {hasResults && (
          <Card>
            <Card.Header>Batch Processing Summary</Card.Header>
            <Card.Body>
              <Row>
                <Col md={3}>
                  <Card className="stat-card bg-light">
                    <div className="stat-value text-success">{completedCount}</div>
                    <div className="stat-label">Completed</div>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="stat-card bg-light">
                    <div className="stat-value text-danger">{errorCount}</div>
                    <div className="stat-label">Failed</div>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="stat-card bg-light">
                    <div className="stat-value">{this.getTotalStats().totalRows.toLocaleString()}</div>
                    <div className="stat-label">Total Rows</div>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="stat-card bg-light">
                    <div className="stat-value">{this.getTotalStats().totalAnomalies}</div>
                    <div className="stat-label">Total Anomalies</div>
                  </Card>
                </Col>
              </Row>
              <div className="mt-3 text-center">
                <Button 
                  variant="outline-primary" 
                  className="mr-2"
                  onClick={() => this.props.history.push('/jobs')}
                >
                  View All Jobs
                </Button>
                <Button 
                  variant="outline-secondary"
                  onClick={this.handleReset}
                >
                  Upload More Files
                </Button>
              </div>
            </Card.Body>
          </Card>
        )}
      </div>
    );
  }
}

export default withRouter(Upload);